import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext} from "@angular/common/http";
import {DEDUPE_KEY} from "../../../x-interceptors/duplicate-request.interceptor";
import {LegalProvider} from "../../legal/_models/_enums/legal-provider";
import {CashPointEntryFilters} from "../_models/cash-point-entry-filters";
import {map, Observable} from "rxjs";
import {CashPointEntryDto} from "../_models/cash-point-entry-dto";
import {ServerEndpoints} from "../../../server.endpoints";
import {BookingType} from "../../../models/payment/booking-type";
import {BusinessCaseInformation} from "../_models/business-case-information";
import {BusinessCaseType} from "../_models/_enums/business-case-type";

@Injectable({
  providedIn: 'root'
})
export class CashPointHttpService {
  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
  }

  list(provider: LegalProvider | undefined, start: Date | undefined, end: Date | undefined, filters: CashPointEntryFilters | undefined): Observable<CashPointEntryDto[]> {
    return this.httpClient.post<CashPointEntryDto[]>(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_List}?provider=${provider ?? ""}&start=${start?.toISOString() ?? ""}&end=${end?.toISOString() ?? ""}`, filters);
  }

  getSummary(device: string, entry: string): Observable<{ [key: number]: number }> {
    return this.httpClient.get<{
      [key: string]: number
    }>(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_GetSummary_$device$.dictReplace({device: device})}?entry=${entry}`).pipe(map((data) => {
      let v: { [key: number]: number } = {};
      Object.keys(data).forEach(key => {
        v[BookingType[key as keyof typeof BookingType]] = data[key];
      });
      return v;
    }));
  }

  getBusinessCaseTypeSummary(device: string, entry: string): Observable<{ [key: number]: number }> {
    return this.httpClient.get<{
      [key: string]: number
    }>(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_GetBusinessCaseTypeSummary_$device$.dictReplace({device: device})}?entry=${entry}`).pipe(map((data) => {
      let v: { [key: number]: number } = {};
      Object.keys(data).forEach(key => {
        v[BusinessCaseType[key as keyof typeof BusinessCaseType]] = data[key];
      });
      return v;
    }));
  }

  getLastClosed(device: string): Observable<{ [key: number]: number }> {
    return this.httpClient.get<{
      [key: string]: string
    }>(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_GetLastClosed_$device$.dictReplace({device: device})}`).pipe(map((data) => {
      let v: { [key: number]: number } = {};
      Object.keys(data).forEach(key => {
        v[BookingType[key as keyof typeof BookingType]] = parseFloat(data[key]);
      });
      return v;
    }));
  }

  summary(ids: string[], combined: boolean = false) {
    return this.httpClient.post(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_Summary}?combined=${combined}`, ids, {
      responseType: "blob",
      observe: "events",
      reportProgress: true
    });
  }

  close(device: string, entry: string, bcs: BusinessCaseInformation[]): Observable<CashPointEntryDto> {
    // Closing a cash-point entry is a one-shot, irreversible action — a double-click must never fire two closes.
    // Dedupe on the device+entry identity rather than the business-case body, which can differ between clicks.
    const context = new HttpContext().set(DEDUPE_KEY, `cashpoint-close:${device}:${entry}`);
    return this.httpClient.put<CashPointEntryDto>(`${this.baseUrl}${ServerEndpoints.Device_CashPointEntry_Close_$device$.dictReplace({device: device})}?entry=${entry}`, bcs, {context});
  }
}
